"use client";

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Dropdown, Form } from 'react-bootstrap';
import { HiOutlineCalendarDays, HiOutlineUsers, HiOutlineMagnifyingGlass, HiOutlineClock, HiOutlineMapPin } from 'react-icons/hi2';
import { FaBus, FaCar } from 'react-icons/fa';
import Flatpicker from '@/components/Flatpicker';
import LanguageSelect from '@/components/form/LanguageSelect';
import { useSiteTrip } from '@/states/useSiteTripContext';
import { defaultHotelTime, lookupHotelRentalGuide } from '@/utils/carRentalHotelTimes';
import { loadCarRentalLocations, peekCarRentalLocations } from '@/utils/carRentalLocationsCache';
import {
  canSearchCarsFromHero,
  nextCarRentalSearchFieldKey,
} from '@/utils/carRentalHeroSearch';
import CarLocationSearchSelect from '@/views/car-rental/components/CarLocationSearchSelect';

function toYmd(d) {
  if (!d) return null;
  const x = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(x.getTime())) return null;
  const y = x.getFullYear();
  const m = String(x.getMonth() + 1).padStart(2, '0');
  const day = String(x.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function formatGuestSummary(g) {
  const total = Math.max(1, Number(g?.adults || 0) + Number(g?.children || 0) + Number(g?.infants || 0));
  return `${total} ${total === 1 ? 'guest' : 'guests'}`;
}

function buildPickupTimeOptions() {
  const slots = [];
  for (let hour = 8; hour <= 23; hour += 1) {
    for (const minute of [0, 30]) {
      if (hour === 23 && minute === 30) continue;
      const hh = String(hour).padStart(2, '0');
      const mm = String(minute).padStart(2, '0');
      slots.push(`${hh}:${mm}`);
    }
  }
  return slots;
}

const PICKUP_TIME_OPTIONS = buildPickupTimeOptions();
const RENTAL_DAYS_OPTIONS = Array.from({ length: 30 }, (_, idx) => idx + 1);
const DEFAULT_GUESTS = { adults: 2, children: 0, infants: 0 };
const DEFAULT_CAR_RENTAL = { days: 1, pickupTime: '10:00', pickupRef: '' };
const GENERIC_HERO_PICKUP_TIME = '10:00';

const Stepper = ({ label, value, min, max, onChange }) => (
  <div className="site-header-trip-search__stepper d-flex align-items-center justify-content-between gap-2 py-1">
    <span className="small text-muted mb-0">{label}</span>
    <div className="d-flex align-items-center gap-2">
      <Button
        type="button"
        variant="light"
        size="sm"
        className="site-header-trip-search__step-btn px-2"
        disabled={value <= min}
        onClick={() => onChange(value - 1)}
        aria-label={`Decrease ${label}`}
      >
        −
      </Button>
      <span className="site-header-trip-search__step-val small fw-semibold tabular-nums">{value}</span>
      <Button
        type="button"
        variant="light"
        size="sm"
        className="site-header-trip-search__step-btn px-2"
        disabled={value >= max}
        onClick={() => onChange(value + 1)}
        aria-label={`Increase ${label}`}
      >
        +
      </Button>
    </div>
  </div>
);

/**
 * Global visit date + guest counts (GetYourGuide-style compact bar).
 * Values persist in localStorage and are available via `useSiteTrip()` on all service pages.
 */
function classList(...parts) {
  return parts
    .flatMap((part) => String(part ?? '').trim().split(/\s+/))
    .filter(Boolean)
    .join(' ');
}

const SiteHeaderTripSearch = ({ className = '', onSearch = null, prefillFromContext = true }) => {
  const {
    visitDateRange,
    serviceType,
    guests,
    carRental,
    languageId,
    setVisitDateRange,
    setServiceType,
    setGuests,
    setCarRental,
    setLanguageId,
  } = useSiteTrip();
  const [dateTouched, setDateTouched] = useState(false);
  const [guestsTouched, setGuestsTouched] = useState(false);
  const [carRentalTouched, setCarRentalTouched] = useState(false);
  const cachedLocations = peekCarRentalLocations();
  const [hotels, setHotels] = useState(() => cachedLocations?.hotels || []);
  const [places, setPlaces] = useState(() => cachedLocations?.places || []);
  const [locationsLoading, setLocationsLoading] = useState(!cachedLocations);

  const hotelsById = useMemo(() => {
    const map = new Map();
    for (const hotel of hotels) {
      const id = Number(hotel?.id);
      if (Number.isFinite(id) && id > 0) map.set(id, hotel);
    }
    return map;
  }, [hotels]);

  useEffect(() => {
    if (serviceType !== 'car_rental') return undefined;
    let cancelled = false;
    setLocationsLoading(!peekCarRentalLocations());
    loadCarRentalLocations()
      .then((payload) => {
        if (cancelled) return;
        setHotels(payload?.hotels || []);
        setPlaces(payload?.places || []);
      })
      .catch(() => {
        if (cancelled) return;
        setHotels([]);
        setPlaces([]);
      })
      .finally(() => {
        if (!cancelled) setLocationsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [serviceType]);

  const effectiveVisitDateRange = useMemo(() => {
    if (!prefillFromContext && !dateTouched) return { start: null, end: null };
    return visitDateRange;
  }, [dateTouched, prefillFromContext, visitDateRange]);

  const effectiveGuests = useMemo(() => {
    if (!prefillFromContext && !guestsTouched) return DEFAULT_GUESTS;
    return guests;
  }, [guests, guestsTouched, prefillFromContext]);

  const handleGuestChange = useCallback((partial) => {
    setGuestsTouched(true);
    setGuests(partial);
  }, [setGuests]);

  const effectiveCarRental = useMemo(() => {
    if (!prefillFromContext && !carRentalTouched) return DEFAULT_CAR_RENTAL;
    return carRental;
  }, [carRental, carRentalTouched, prefillFromContext]);

  const handleCarRentalChange = useCallback((partial) => {
    setCarRentalTouched(true);
    setCarRental(partial);
  }, [setCarRental]);

  const handlePickupRefChange = useCallback((ref) => {
    const pickupRef = String(ref || '').trim();
    const next = { pickupRef };
    const guide = lookupHotelRentalGuide(hotelsById, pickupRef);
    const currentTime = effectiveCarRental.pickupTime;
    if (!currentTime || currentTime === GENERIC_HERO_PICKUP_TIME) {
      const hotelTime = defaultHotelTime(guide?.deliveryTimes);
      if (hotelTime) next.pickupTime = hotelTime;
    }
    handleCarRentalChange(next);
  }, [effectiveCarRental.pickupTime, handleCarRentalChange, hotelsById]);

  const dateRangeValue = useMemo(() => {
    if (!effectiveVisitDateRange?.start) return null;
    const start = new Date(`${effectiveVisitDateRange.start}T12:00:00`);
    return Number.isNaN(start.getTime()) ? null : start;
  }, [effectiveVisitDateRange?.start]);

  const onDatePick = useCallback(
    (selectedDateValue) => {
      setDateTouched(true);
      const start = selectedDateValue instanceof Date
        ? selectedDateValue
        : (Array.isArray(selectedDateValue) && selectedDateValue[0] instanceof Date
          ? selectedDateValue[0]
          : null);
      setVisitDateRange({
        start: start ? toYmd(start) : null,
        end: null,
      });
    },
    [setVisitDateRange]
  );

  const canSubmit = serviceType !== 'car_rental' || canSearchCarsFromHero({
    pickupDate: effectiveVisitDateRange?.start,
    pickupRef: effectiveCarRental.pickupRef,
  });
  const nextCarFieldKey = nextCarRentalSearchFieldKey({
    pickupDate: effectiveVisitDateRange?.start,
    pickupRef: effectiveCarRental.pickupRef,
  });

  const focusHeroCarField = useCallback((key) => {
    const selectors = {
      pud: '#site-header-car-pickup-date .flatpickr, #site-header-car-pickup-date input',
      pul: '#site-header-car-pickup-location',
    };
    const el = typeof document !== 'undefined'
      ? document.querySelector(selectors[key] || '')
      : null;
    if (!el) return;
    el.focus?.();
    if (typeof el.click === 'function') el.click();
  }, []);

  const emitSearch = useCallback(() => {
    if (!canSubmit) return;
    onSearch?.({
      serviceType,
      visitDateRange: effectiveVisitDateRange,
      guests: effectiveGuests,
      carRental: effectiveCarRental,
      languageId,
    });
  }, [
    canSubmit,
    effectiveCarRental,
    effectiveGuests,
    effectiveVisitDateRange,
    languageId,
    onSearch,
    serviceType,
  ]);

  const handleSubmit = useCallback((event) => {
    event.preventDefault();
    if (!canSubmit) {
      focusHeroCarField(nextCarFieldKey);
      return;
    }
    emitSearch();
  }, [canSubmit, emitSearch, focusHeroCarField, nextCarFieldKey]);

  return (
    <form
      className={classList('site-header-trip-search', 'd-flex', 'align-items-stretch', 'flex-wrap', 'gap-2', className)}
      onSubmit={handleSubmit}
      noValidate
    >
      <div className="site-header-trip-search__field site-header-trip-search__field--service p-1">
        <div className="site-header-trip-search__service-switch d-inline-flex align-items-stretch">
          <button
            type="button"
            className={classList('site-header-trip-search__service-btn', serviceType === 'excursions' ? 'active' : null)}
            onClick={() => setServiceType('excursions')}
          >
            <FaBus size={20} aria-hidden />
            <span>Excursions</span>
          </button>
          <button
            type="button"
            className={classList('site-header-trip-search__service-btn', serviceType === 'car_rental' ? 'active' : null)}
            onClick={() => setServiceType('car_rental')}
          >
            <FaCar size={20} aria-hidden />
            <span>Car Rental</span>
          </button>
        </div>
      </div>

      <div
        id={serviceType === 'car_rental' ? 'site-header-car-pickup-date' : undefined}
        className="site-header-trip-search__field site-header-trip-search__field--date flex-grow-1"
      >
        <span className="site-header-trip-search__icon" aria-hidden>
          <HiOutlineCalendarDays size={20} strokeWidth={1.75} />
        </span>
        <Flatpicker
          className="form-control form-control-sm site-header-trip-search__input border-0 shadow-none"
          placeholder={serviceType === 'car_rental' ? 'Pickup date' : 'Tour Date'}
          value={dateRangeValue}
          getValue={onDatePick}
          options={{
            minDate: 'today',
            dateFormat: 'l, M j',
            allowInput: false,
          }}
        />
      </div>

      {serviceType === 'excursions' ? (
        <>
          <Dropdown align="end" autoClose="outside">
            <Dropdown.Toggle
              variant="light"
              id="site-header-guests"
              className="site-header-trip-search__field site-header-trip-search__field--guests d-flex align-items-center gap-2 text-start text-body shadow-none"
            >
              <span className="site-header-trip-search__icon flex-shrink-0" aria-hidden>
                <HiOutlineUsers size={20} strokeWidth={1.75} />
              </span>
              <span className="small text-truncate">{formatGuestSummary(effectiveGuests)}</span>
            </Dropdown.Toggle>
            <Dropdown.Menu className="site-header-trip-search__guest-menu p-3 shadow border-0">
              <div className="small fw-semibold text-uppercase text-muted mb-2 letter-spacing-tight">
                Guests
              </div>
              <Stepper
                label="Adults"
                value={effectiveGuests.adults}
                min={1}
                max={20}
                onChange={(v) => handleGuestChange({ adults: v })}
              />
              <Stepper
                label="Children"
                value={effectiveGuests.children}
                min={0}
                max={20}
                onChange={(v) => handleGuestChange({ children: v })}
              />
              <Stepper
                label="Infants"
                value={effectiveGuests.infants}
                min={0}
                max={10}
                onChange={(v) => handleGuestChange({ infants: v })}
              />
              <Button
                variant="primary"
                size="sm"
                className="w-100 mt-2"
                type="button"
              >
                Done
              </Button>
            </Dropdown.Menu>
          </Dropdown>
          <div className="site-header-trip-search__field site-header-trip-search__field--language">
            <LanguageSelect
              value={languageId}
              onChange={(value) => setLanguageId(value)}
              placeholder="All Languages"
              allOptionLabel="All Languages"
              showIcon={false}
              showLabel={false}
              className="site-header-trip-search__language-toggle"
            />
          </div>
        </>
      ) : (
        <>
          <div className="site-header-trip-search__field site-header-trip-search__field--rental-time">
            <span className="site-header-trip-search__icon flex-shrink-0" aria-hidden>
              <HiOutlineClock size={20} strokeWidth={1.75} />
            </span>
            <Form.Select
              value={effectiveCarRental.pickupTime}
              className="site-header-trip-search__select form-select-sm border-0 shadow-none"
              aria-label="Pickup time"
              onChange={(e) => handleCarRentalChange({ pickupTime: e.target.value })}
            >
              {PICKUP_TIME_OPTIONS.map((time) => (
                <option key={time} value={time}>{time}</option>
              ))}
            </Form.Select>
          </div>
          <div className="site-header-trip-search__field site-header-trip-search__field--rental-days">
            <span className="site-header-trip-search__icon flex-shrink-0" aria-hidden>
              <HiOutlineUsers size={20} strokeWidth={1.75} />
            </span>
            <Form.Select
              value={effectiveCarRental.days}
              className="site-header-trip-search__select form-select-sm border-0 shadow-none"
              aria-label="Days of rental"
              onChange={(e) => handleCarRentalChange({ days: Number(e.target.value) })}
            >
              {RENTAL_DAYS_OPTIONS.map((days) => (
                <option key={days} value={days}>
                  {days} {days === 1 ? 'day' : 'days'}
                </option>
              ))}
            </Form.Select>
          </div>
          <div className="site-header-trip-search__field site-header-trip-search__field--rental-location">
            <span className="site-header-trip-search__icon flex-shrink-0" aria-hidden>
              <HiOutlineMapPin size={20} strokeWidth={1.75} />
            </span>
            <CarLocationSearchSelect
              bare
              id="site-header-car-pickup-location"
              value={effectiveCarRental.pickupRef || ''}
              onChange={handlePickupRefChange}
              hotels={hotels}
              places={places}
              loading={locationsLoading}
              placeholder="Pick-up location"
              searchPlaceholder="Search hotels, places"
              aria-label="Pick-up location"
            />
          </div>
        </>
      )}
      <Button
        type="submit"
        variant="primary"
        className="site-header-trip-search__search-btn d-inline-flex align-items-center justify-content-center px-0"
        aria-label="Search"
        title="Search"
      >
        <HiOutlineMagnifyingGlass size={22} strokeWidth={1.6} />
      </Button>
    </form>
  );
};

export default SiteHeaderTripSearch;
